import type { FormlyFieldConfig } from '@ngx-formly/core';
import { get } from 'lodash-es';
import { DateTime } from 'luxon';

import { UtilityService } from '../../services/utility.service';
import { extractOptionValue, resolveOptionBadge } from '../../utils/option-badge.util';
import type { ResolvedOptionBadge } from '../status-badge.types';
import type { TaxonomyLabelStore } from './readonly/readonly.types';
import {
  AutomaticCheckToneEnum,
  type AutomaticCheckRow,
  type AutomaticCheckText,
  type AutomaticCheckTone,
  type AutomaticChecksPaths,
  type RdwCheckState,
  type RdwCheckSummary,
} from './automatic-checks.types';

export const AUTOMATIC_CHECKS_DEFAULT_ASSETS_MODULE = 'assets';

export const AUTOMATIC_CHECKS_DEFAULT_PATHS: Readonly<AutomaticChecksPaths> = {
  kvkStatus: 'meta-data.kvk-status',
  kvkLastCheck: 'meta-data.kvk-last-check-date',
  kvkNextCheck: 'meta-data.kvk-next-check-date',
  kvkNumber: 'general.chamber-of-commerce-number',
  assetCompany: 'general.company',
  assetPlateLookedUp: 'general.assets-vehicle-general.license-plate.lookedUp',
  rdwLastCheck: 'meta-data.rdw-last-check-date',
  rdwNextCheck: 'meta-data.rdw-next-check-date',
  insured: 'general.vehicle-inspection.insured',
};

const NOT_CHECKED_YET = 'Nog niet gecontroleerd';
const SEPARATOR = ' · ';
/**
 * Neutral pill for "no status yet", with the muted text colour instead of the neutral badge's
 * full-contrast foreground: in dark mode that foreground is white and reads louder than a real
 * status. Only this pill; the shared token stays as it is.
 */
const NOT_CHECKED_BADGE: ResolvedOptionBadge = {
  label: NOT_CHECKED_YET,
  customColors: { bg: 'var(--dd-badge-neutral-bg)', fg: 'var(--dd-card-muted-fg)' },
};
/** The option label that reads as a valid registration when the option itself carries no colour. */
const VALID_KVK_LABEL = 'geldig';

/** Merges config overrides over the defaults, ignoring empty values. */
export function resolveCheckPaths(overrides: Partial<AutomaticChecksPaths> | undefined): AutomaticChecksPaths {
  const paths = { ...AUTOMATIC_CHECKS_DEFAULT_PATHS };
  for (const [key, value] of Object.entries(overrides ?? {})) {
    if (typeof value === 'string' && value.trim() !== '') {
      paths[key as keyof AutomaticChecksPaths] = value.trim();
    }
  }
  return paths;
}

/**
 * ISO date string → dd-MM-yyyy in the viewer's zone: a timestamp such as `2026-10-23T22:00:00Z` is
 * the 24th in the Netherlands. A date-only string (`2026-10-24`) parses as local midnight, so it
 * keeps its own day without any zone handling.
 */
export function formatCheckDate(iso: unknown): string | undefined {
  if (typeof iso !== 'string' || iso === '') {
    return undefined;
  }
  const date = DateTime.fromISO(iso);
  return date.isValid ? date.toFormat('dd-MM-yyyy') : undefined;
}

/** The KvK number from the `kvk` field value: `{ kvkNumber }`, or the bare number in older data. */
export function readKvkNumber(raw: unknown): string | undefined {
  const value = raw != null && typeof raw === 'object' ? (raw as { kvkNumber?: unknown }).kvkNumber : raw;
  if (typeof value === 'number') {
    return String(value);
  }
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : undefined;
}

/**
 * "Laatste controle … · volgende …", plus any extra parts; undefined when nothing is known. A next
 * date already behind `now` leads the line instead, in the same muted detail text as the design:
 * "Controle achterstallig sinds … · laatste controle …".
 */
function checkDetail(
  lastIso: unknown,
  nextIso: unknown,
  now: Date,
  ...extra: (string | undefined)[]
): AutomaticCheckText[] | undefined {
  const last = formatCheckDate(lastIso);
  const next = formatCheckDate(nextIso);
  const overdue = next !== undefined && (toTime(nextIso) ?? Infinity) < now.getTime();
  const parts: AutomaticCheckText[] = [];
  if (overdue) {
    // Without an earlier check nothing is "behind" yet: the planned first check simply did not run.
    const warning = last ? `Controle achterstallig sinds ${next}` : `Controle gepland op ${next}, nog niet uitgevoerd`;
    parts.push({ text: warning });
    if (last) {
      parts.push({ text: `laatste controle ${last}` });
    }
  } else {
    if (last) {
      parts.push({ text: `Laatste controle ${last}` });
    }
    if (next) {
      parts.push({ text: last ? `volgende ${next}` : `Volgende controle ${next}` });
    }
  }
  parts.push(...extra.filter((part): part is string => !!part).map(text => ({ text })));
  return parts.length ? joinParts(parts) : undefined;
}

/** Interleaves the separator, keeping each part's own tone. */
function joinParts(parts: AutomaticCheckText[]): AutomaticCheckText[] {
  return parts.flatMap((part, index) => (index === 0 ? [part] : [{ text: SEPARATOR }, part]));
}

/**
 * The KvK status badge: the option's own colour when it has one (the one badge rule), otherwise
 * positive for a status that reads "Geldig" and neutral for anything else. No status at all is a
 * neutral "Nog niet gecontroleerd" pill, so the KvK row always shows a badge.
 */
export function kvkStatusBadge(
  raw: unknown,
  statusField: FormlyFieldConfig | undefined,
  store: TaxonomyLabelStore | undefined
): ResolvedOptionBadge {
  const extracted = extractOptionValue(raw);
  if (!extracted) {
    return NOT_CHECKED_BADGE;
  }
  const optionBadge = resolveOptionBadge(statusField, raw, store);
  if (optionBadge) {
    return { ...optionBadge, label: extracted.label || optionBadge.label };
  }
  const isValid = extracted.label.trim().toLowerCase() === VALID_KVK_LABEL;
  return { label: extracted.label, variant: isValid ? 'positive' : 'neutral' };
}

/** The KvK row, read from the company entry's own values. */
export function buildKvkRow(
  model: unknown,
  paths: AutomaticChecksPaths,
  now: Date,
  statusField?: FormlyFieldConfig,
  store?: TaxonomyLabelStore
): AutomaticCheckRow {
  const kvkNumber = readKvkNumber(get(model, paths.kvkNumber));
  return {
    label: 'KvK',
    badge: kvkStatusBadge(get(model, paths.kvkStatus), statusField, store),
    detail: checkDetail(
      get(model, paths.kvkLastCheck),
      get(model, paths.kvkNextCheck),
      now,
      kvkNumber && `KvK ${kvkNumber}`
    ),
  };
}

function toTime(iso: unknown): number | undefined {
  if (typeof iso !== 'string' || iso === '') {
    return undefined;
  }
  const time = Date.parse(iso);
  return Number.isNaN(time) ? undefined : time;
}

/** Picks the ISO string with the extreme timestamp (`pick` = Math.max or Math.min). */
function extremeIso(values: unknown[], pick: (a: number, b: number) => number): string | undefined {
  let best: { time: number; iso: string } | undefined;
  for (const value of values) {
    const time = toTime(value);
    if (time !== undefined && (!best || pick(time, best.time) === time)) {
      best = { time, iso: value as string };
    }
  }
  return best?.iso;
}

/**
 * Aggregates the RDW checks over a company's looked-up assets (each item is an asset's `entry`
 * object). An asset counts as checked while its next check date is still ahead: a failed monthly
 * check leaves the old date behind, so the asset drops out of `checked` on its own.
 */
export function summarizeRdwChecks(assets: unknown[], now: Date, paths: AutomaticChecksPaths): RdwCheckSummary {
  const nowTime = now.getTime();
  const checkedAssets = assets.filter(asset => (toTime(get(asset, paths.rdwNextCheck)) ?? -Infinity) >= nowTime);
  const allNext = assets.map(asset => get(asset, paths.rdwNextCheck));
  return {
    total: assets.length,
    checked: checkedAssets.length,
    uninsured: assets.filter(asset => get(asset, paths.insured) === 'Nee').length,
    lastCheck: extremeIso(
      assets.map(asset => get(asset, paths.rdwLastCheck)),
      Math.max
    ),
    nextCheck:
      extremeIso(
        checkedAssets.map(asset => get(asset, paths.rdwNextCheck)),
        Math.min
      ) ?? extremeIso(allNext, Math.max),
  };
}

function plates(count: number): string {
  return count === 1 ? 'kenteken' : 'kentekens';
}

/** The RDW row for each state of the live fetch. */
export function buildRdwRow(state: RdwCheckState, now: Date): AutomaticCheckRow {
  const label = 'RDW';
  const text = (headline: string, tone?: AutomaticCheckTone): AutomaticCheckRow => ({
    label,
    headline: [{ text: headline, tone }],
  });

  switch (state.status) {
    case 'loading':
      return { label, loading: true, loadingLabel: 'RDW-controle wordt geladen' };
    case 'error': {
      if (state.forbidden) {
        return {
          ...text('Geen toegang tot voertuiggegevens', AutomaticCheckToneEnum.Muted),
          detail: [{ text: 'Vraag een beheerder om leesrechten op voertuiggegevens' }],
        };
      }
      const last = formatCheckDate(state.lastCheck);
      return {
        ...text('RDW-status kon niet worden opgehaald', AutomaticCheckToneEnum.Negative),
        detail: joinParts([
          { text: 'Probeer het later opnieuw' },
          ...(last ? [{ text: `laatste controle ${last}` }] : []),
        ]),
      };
    }
    case 'idle':
      return text('Wordt gecontroleerd na het opslaan', AutomaticCheckToneEnum.Muted);
  }

  const { total, checked, uninsured, lastCheck, nextCheck } = state.summary;
  if (total === 0) {
    return text('Geen kentekens om te controleren');
  }
  if (checked === 0) {
    return text(NOT_CHECKED_YET, AutomaticCheckToneEnum.Muted);
  }
  const count = checked === total ? `${total} ${plates(total)}` : `${checked} van ${total} ${plates(total)}`;
  const uninsuredText = `${uninsured} niet verzekerd`;
  return {
    label,
    headline: [
      { text: `${count} gecontroleerd${SEPARATOR}` },
      uninsured > 0
        ? { text: uninsuredText, tone: AutomaticCheckToneEnum.Negative, strong: true }
        : { text: uninsuredText },
    ],
    detail: checkDetail(lastCheck, nextCheck, now, 'verzekering en tenaamstelling'),
  };
}

/** OData filter for a company's assets whose plate was looked up at the RDW. */
export function buildAssetsFilter(companyId: string, paths: AutomaticChecksPaths): string {
  const company = UtilityService.dotPathToObjectPath(`entry.${paths.assetCompany}.id`);
  const lookedUp = UtilityService.dotPathToObjectPath(`entry.${paths.assetPlateLookedUp}`);
  return `${company} eq '${companyId.replace(/'/g, "''")}' and ${lookedUp} eq true`;
}
